import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAnniversary } from '../../context/AnniversaryContext';
import Section from '../common/Section';

const PhotocardsSection: React.FC = () => {
  const { data } = useAnniversary();
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  const [isCollecting, setIsCollecting] = useState(false);
  const [isCollected, setIsCollected] = useState(false);
  
  const handleCardClick = (cardId: string) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };
  
  const collectAllCards = () => {
    if (isCollecting) return; // Prevent multiple clicks during animation
    
    setIsCollecting(true);
    setIsCollected(true);
    
    // Reset after animation completes
    setTimeout(() => {
      setIsCollecting(false);
    }, 1500);
  };
  
  const resetCollection = () => {
    setIsCollected(false);
    setFlippedCards(new Set());
  };
  
  return (
    <Section id="photocards" title="Limited Edition Photocards">
      <PhotocardsDescription
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <p>K-pop style collectible photocards of our special moments together.</p>
        <ButtonContainer>
          <CollectButton 
            onClick={collectAllCards}
            disabled={isCollecting}
            as={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isCollecting ? 'Collecting...' : 'Collect All Cards'}
          </CollectButton>
          {isCollected && !isCollecting && (
            <ResetButton 
              onClick={resetCollection}
              as={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
            >
              Scatter Cards
            </ResetButton>
          )}
        </ButtonContainer>
      </PhotocardsDescription>
      
      <PhotocardsContainer isCollected={isCollected}>
        {data.photocards.map((card, index) => (
          <Photocard
            key={card.id}
            as={motion.div}
            custom={index}
            initial={false}
            animate={
              isCollected ? {
                // Collected state - organized in a grid
                x: 0,
                y: 0,
                rotate: 0,
                scale: 1,
                zIndex: flippedCards.has(card.id) ? 100 : 1,
                transition: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                  delay: index * 0.05
                }
              } : {
                // Scattered state - better spaced positions
                x: (index % 3 - 1) * 200 + (Math.sin(index * 1.7) * 80),
                y: (Math.floor(index / 3) % 2 - 0.5) * 180 + (Math.cos(index * 2.1) * 60),
                rotate: (index % 2 === 0 ? 1 : -1) * (3 + (index % 6)),
                scale: 1,
                zIndex: flippedCards.has(card.id) ? 100 : 1,
                transition: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                  delay: isCollecting ? 0 : index * 0.02
                }
              }
            }
            onClick={() => !isCollecting && handleCardClick(card.id)}
            className={isCollected ? 'collected' : 'scattered'}
            style={{ 
              position: isCollected ? 'relative' : 'absolute'
            }}
          >
            <PhotocardInner flipped={flippedCards.has(card.id)}>
              <PhotocardFront>
                {flippedCards.has(card.id) ? (
                  <>
                    <PhotocardFrontImage 
                      as="img"
                      src={card.frontImage}
                      alt={card.title}
                      style={{ height: '70%' }}
                    />
                    <PhotocardTextSection>
                      <PhotocardTitle>
                        <KoreanTitle>{card.koreanTitle || card.title}</KoreanTitle>
                        <EnglishTitle>{card.subtitle}</EnglishTitle>
                      </PhotocardTitle>
                      <PhotocardDescription>{card.description}</PhotocardDescription>
                    </PhotocardTextSection>
                  </>
                ) : (
                  <PhotocardFrontImage 
                    style={{ 
                      backgroundColor: `hsl(${index * 40 % 360}, 70%, 75%)` // Colorful placeholders
                    }}
                  />
                )}
                <PhotocardFrame />
                <PhotocardDecoration position="top-left" />
                <PhotocardDecoration position="top-right" />
                <PhotocardDecoration position="bottom-left" />
                <PhotocardDecoration position="bottom-right" />
              </PhotocardFront>
            </PhotocardInner>
          </Photocard>
        ))}
      </PhotocardsContainer>
      
      <CollectionGuide>
        <motion.h3
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          How to collect
        </motion.h3>
        <GuideSteps>
          <GuideStep
            as={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <StepNumber>1</StepNumber>
            <StepText>Click each card to flip and see the details</StepText>
          </GuideStep>
          <GuideStep
            as={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <StepNumber>2</StepNumber>
            <StepText>Press "Collect All Cards" to arrange in order</StepText>
          </GuideStep>
          <GuideStep
            as={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <StepNumber>3</StepNumber>
            <StepText>Enjoy our special memories together</StepText>
          </GuideStep>
        </GuideSteps>
      </CollectionGuide>
    </Section>
  );
};

const PhotocardsDescription = styled(motion.div)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing.md};
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CollectButton = styled(motion.button)`
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  border: none;
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const ResetButton = styled(motion.button)`
  background: ${({ theme }) => theme.colors.dark};
  color: white;
  border: 2px solid ${({ theme }) => theme.colors.accent1};
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    background: ${({ theme }) => theme.colors.accent1};
    color: white;
  }
`;

const PhotocardsContainer = styled.div<{ isCollected: boolean }>`
  display: ${props => props.isCollected ? 'grid' : 'block'};
  grid-template-columns: ${props => props.isCollected ? 'repeat(auto-fit, minmax(250px, 1fr))' : 'none'};
  gap: ${({ theme }) => theme.spacing.xl};
  margin: -${({ theme }) => theme.spacing.xl} 0 0 0;
  perspective: 1000px;
  height: ${props => props.isCollected ? 'auto' : '875px'};
  position: relative;
  padding: ${props => props.isCollected ? props.theme.spacing.xl : '0'};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: ${props => props.isCollected ? 'auto' : '900px'};
    grid-template-columns: ${props => props.isCollected ? 'repeat(auto-fit, minmax(200px, 1fr))' : 'none'};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: ${props => props.isCollected ? 'auto' : '800px'};
    grid-template-columns: ${props => props.isCollected ? 'repeat(auto-fit, minmax(150px, 1fr))' : 'none'};
  }
`;

const Photocard = styled(motion.div)`
  width: 325px;
  height: 455px;
  cursor: pointer;
  transform-style: preserve-3d;
  
  &.collected {
    width: 100%;
    max-width: 325px;
    margin: 0 auto;
  }
  
  &.scattered {
    position: absolute;
    top: 20%;
    left: 50%;
    transform-origin: center;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 260px;
    height: 364px;
    
    &.collected {
      max-width: 260px;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 195px;
    height: 273px;
    
    &.collected {
      max-width: 195px;
    }
  }
`;

const PhotocardInner = styled.div<{ flipped: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.3s ease;
`;

const PhotocardFront = styled.div`
  width: 100%;
  height: 100%;
  background-color: white;
  padding: 10px;
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
`;

const PhotocardFrontImage = styled.div`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.borderRadius.small};
`;

const PhotocardFrame = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 5px solid white;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  pointer-events: none;
`;

const PhotocardDecoration = styled.div<{ position: string }>`
  position: absolute;
  width: 15px;
  height: 15px;
  ${props => {
    switch (props.position) {
      case 'top-left':
        return 'top: 5px; left: 5px;';
      case 'top-right':
        return 'top: 5px; right: 5px;';
      case 'bottom-left':
        return 'bottom: 5px; left: 5px;';
      case 'bottom-right':
        return 'bottom: 5px; right: 5px;';
      default:
        return '';
    }
  }}
  &::before, &::after {
    content: '';
    position: absolute;
    background-color: ${({ theme }) => theme.colors.accent1};
  }
  &::before {
    width: 100%;
    height: 2px;
    top: 50%;
    transform: translateY(-50%);
  }
  &::after {
    width: 2px;
    height: 100%;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const KoreanTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.korean};
  font-size: 1.5rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.2rem;
    margin-bottom: 0.25rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    margin-bottom: 0.2rem;
  }
`;

const EnglishTitle = styled.h4`
  font-size: 1rem;
  margin-bottom: 0;
  opacity: 0.9;
  text-align: center;
  line-height: 1.2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 0.85rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.75rem;
  }
`;

const PhotocardTitle = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 0.4rem;
  }
`;

const PhotocardDescription = styled.p`
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.5;
  opacity: 1.0;
  position: relative;
  z-index: 2;
  margin: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 0.8rem;
    line-height: 1.3;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.7rem;
    line-height: 1.2;
  }
`;

const PhotocardTextSection = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  height: 30%;
  background: ${({ theme }) => theme.gradients.card};
  color: white;
  padding: 15px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 35%;
    padding: 12px;
    bottom: 8px;
    left: 8px;
    right: 8px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 40%;
    padding: 8px;
    bottom: 6px;
    left: 6px;
    right: 6px;
  }
`;

const CollectionGuide = styled.div`
  margin-top: 0;
  text-align: center;
  
  h3 {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    display: inline-block;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      height: 2px;
      width: 60%;
      background: ${({ theme }) => theme.colors.accent2};
      bottom: -8px;
      left: 20%;
    }
  }
`;

const GuideSteps = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  max-width: 900px;
  margin: 0 auto;
`;

const GuideStep = styled(motion.div)`
  flex: 1;
  min-width: 200px;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StepText = styled.p`
  font-size: 0.9rem;
  opacity: 0.9;
`;

export default PhotocardsSection; 